import { Box, Container, Flex, Grid, GridItem, Text, useMediaQuery } from '@chakra-ui/react';
// import { mode } from '@chakra-ui/theme-tools';
import { nodeParser } from '@kontent-ai/delivery-node-parser';
import { createRichTextHtmlResolver } from '@kontent-ai/delivery-sdk';
import OfficeInfo from 'components/pages/ContactUs/OfficeInfo';
import { useRouter } from 'next/router';
import React, { FunctionComponent } from 'react';
import { withTranslations } from 'store/translations';
import { moduleMaxWidth } from 'themes/overrides';
import { getLinkToMore } from 'utils/linkToMore';
import { normalizeEditorialSpotItems } from 'utils/normalization/editorialSpot';

import { colorScheme, getColorScheme } from '../../lib/colorScheme';
import { LinkType } from '../../lib/enums/LinkType';
import {
  Contact,
  ContentPage,
  EditorialSpots as EditorialSpotsModel,
  Office,
  PDFEditorialSpotItem,
} from '../../models';
import { Link } from '../base/Link';
import { HyphenResult } from '../hyphen/hyphenation';
import { OverviewItem } from './OverviewItem';
import { PersonOverviewItem } from './PersonOverviewItem';

interface EditorialSpotsProps {
  model: EditorialSpotsModel;
}

export const EditorialSpots: FunctionComponent<EditorialSpotsProps> = ({ model }) => {
  const translations = withTranslations();
  const [isSmallScreen] = useMediaQuery('(max-width: 1024px)');
  const router = useRouter();
  const slug = router.query.slug || router.query.mainSubpage || router.query.subcategory;
  const scheme = colorScheme[model.elements.backgroundColor?.value[0].codename as keyof typeof colorScheme];
  const paddings = model.elements.paddings?.value.map((value) => value.codename);
  const items = normalizeEditorialSpotItems(model, translations, slug as string[]);
  const headlineColor = getColorScheme(
    // model.elements.backgroundColor?.value[0]?.codename,
    model.elements.headlineColor?.value[0]?.codename || 'mountain',
  );
  // const enableHyphenHeading = model.elements.enableHyphenationForHeading.value[0]?.codename == 'yes';
  // const enableHyphenSummary = model.elements.enableHyphenationForSummary.value[0]?.codename == 'yes';

  //TODO: after getting localization from url, apply different localization, or process.env.NEXT_PUBLIC_DEFAULT_LOCALE! if empty
  const subHeadline = model.elements.subheadline?.value;
  const linkText =
    model.elements.category.value[0] === undefined
      ? ''
      : translations.multiple(model.elements.category.value[0].codename);
  const ariaLinkText = model.elements.cta.value && model.elements.cta.value + ` ${linkText}`;

  const formModalButtonText = model.elements.formModalCtaButtonText?.value || '';
  const formModal = model.elements.formModalCtaFormModal?.linkedItems;
  const shouldShowPeople = model.elements.category.value[0]?.codename === 'people' && items && items.length > 0;
  const shouldShowOffices = model.elements.category.value[0]?.codename === 'office' && items && items.length;
  const linkToMore = getLinkToMore(
    model.elements.category.value[0]?.codename,
    model.elements.showcasedContent.linkedItems[0] as ContentPage | Contact | Office | PDFEditorialSpotItem,
    translations,
    model.elements.ctaTaxonomySegment,
  );

  // console.log('editorial item ',model)
  const hyphenHeadline = HyphenResult(model.elements.headline.value, router.locale);
  const showCountry = model.elements.showCountryForContact.value[0]?.codename;
  const getOverviewItems = (personItemAdjustWidth?: boolean) => {
    if (shouldShowPeople) {
      return items.map((person, index) => (
        <GridItem
          key={index}
          as='li'
          flexGrow={1}
          w={personItemAdjustWidth ? 'min-content' : ''}
          //_first={{ paddingLeft: '0' }}
          px={{ base: 'desktop.xs', lg: 0 }}
          //mt={{ base: 'desktop.xs', lg: 'desktop.s' }}
        >
          <PersonOverviewItem
            editorialSpotsId={model.system.id}
            contact={person as Contact}
            formModalButtonText={formModalButtonText}
            formModal={formModal[0]}
            showCountry={showCountry}
          />
        </GridItem>
      ));
    }
    if (shouldShowOffices) {
      return items.map((office, index) => {
        const officeModel = office as Office;
        return (
          <GridItem key={index} as='li' flexGrow={1} px={{ base: 'desktop.xs', lg: 0 }} _first={{ paddingLeft: '0' }}>
            <OfficeInfo
              address={
                createRichTextHtmlResolver(nodeParser).resolveRichText({
                  element: officeModel.elements.address,
                }).html
              }
              city={officeModel.elements.city.value}
              country={officeModel.elements.country.value[0].name}
              cvr={officeModel.elements.taxNumber.value}
              description={
                createRichTextHtmlResolver(nodeParser).resolveRichText({
                  element: officeModel.elements.info,
                }).html
              }
              email={officeModel.elements.email.value}
              id={officeModel.system.id}
              isMain={!!officeModel.elements.isHeadOffice.value[0]?.name}
              officeName={officeModel.elements.officeName.value || null}
              phone={officeModel.elements.phoneNumber.value}
            />
          </GridItem>
        );
      });
    }
    return items.map((item, index) => (
      <GridItem
        key={index}
        as='li'
        flexGrow={1}
        px={{ base: items.length < 3 ? 0 : 'desktop.xs', lg: 0 }}
        _first={{ paddingLeft: '0' }}
      >
        <OverviewItem
          model={item}
          singleItem={items.length === 1}
          numberOfItems={items.length}
          hideTag={model.elements.hideTag?.value[0]?.codename}
          backgroundColor={items.length === 1 ? scheme.base : scheme.base}
          // enableHyphenHeading={enableHyphenHeading}
          // enableHyphenSummary={enableHyphenSummary}
        />
      </GridItem>
    ));
  };
  const setPaddings = () => {
    const top = paddings.filter((item) => item.includes('top')).join(',');
    const bottom = paddings.filter((item) => item.includes('bottom')).join(',');
    return {
      pt: top.includes('all')
        ? 0
        : top.includes('half')
        ? { base: 'mobile.m', lg: 'desktop.s' }
        : { base: 'mobile.l', lg: 'desktop.l' },
      pb: bottom.includes('all')
        ? 0
        : bottom.includes('half')
        ? { base: 'mobile.m', lg: 'desktop.s' }
        : { base: 'mobile.l', lg: 'desktop.l' },
    };
  };

  return (
    <>
      <Box
        id={model.system.codename}
        w='100vw'
        maxW={moduleMaxWidth}
        mx='auto'
        sx={{
          bgGradient:
            items.length === 1 &&
            'mediaPosition' in items[0] &&
            model.elements.backgroundWidth?.value[0]?.codename === 'full'
              ? {} // No gradient for full background when single item
              : items.length === 1
              ? {
                  base: `linear(to-b, ${scheme.base} 30%, white 0%)`,
                  sm: `linear(to-b, ${scheme.base} 40%, white 0%)`,
                  md: `linear(to-b, ${scheme.base} 45%, white 0%)`,
                  lg:
                    model.elements.mediaPosition?.value[0]?.codename === 'start'
                      ? `linear(to-r, white 50%, ${scheme.base} 0%)`
                      : `linear(to-r, ${scheme.base} 50%, white 0%)`,
                  xl:
                    model.elements.mediaPosition?.value[0]?.codename === 'start'
                      ? `linear(to-r, white 55%, ${scheme.base} 0%)`
                      : `linear(to-r, ${scheme.base} 55%, white 0%)`,
                }
              : {}, // Apply solid background for multiple items
        }}
      >
        {
          <Container
            position='relative'
            display={'flex'}
            flexDir='row'
            flexWrap={'wrap'}
            sx={setPaddings()}
            bgGradient={
              items.length === 1 &&
              'mediaPosition' in items[0] &&
              model.elements.backgroundWidth?.value[0]?.codename === 'partial'
                ? {
                    base: `linear(to-b, ${scheme.base} 30%, white 0%)`,
                    sm: `linear(to-b, ${scheme.base} 40%, white 0%)`,
                    md: `linear(to-b, ${scheme.base} 45%, white 0%)`,
                    lg:
                      items[0].mediaPosition === 'start'
                        ? `linear(to-r, white 50%, ${scheme.base} 0%)`
                        : `linear(to-r, ${scheme.base} 50%, white 0%)`,
                    xl:
                      items[0].mediaPosition === 'start'
                        ? `linear(to-r, white 55%, ${scheme.base} 0%)`
                        : `linear(to-r, ${scheme.base} 55%, white 0%)`,
                  }
                : {}
            }
            // px={{ base: '0', lg: 'l' }}
            bg={
              items.length === 1 && model.elements.backgroundWidth?.value[0]?.codename === 'full'
                ? scheme.base
                : scheme.base
            }
          >
            {/* --------- HEADLINE AND VIEW MORE ---------  */}
            <Flex
              flexDir={'column'}
              alignItems='flex-start'
              justifyContent='space-between'
              //gap={{ base: 0, lg: 'xs' }}
              flex={1}
              order={1}
              //p={items.length === 1 ? { base: 'xs', lg: 's' } : 0}
            >
              {!!model.elements.headline.value.length && (
                <Box display={'inline-flex'}>
                  <Box
                    w={'2px'}
                    mr={'desktop.xs'}
                    height={'auto'}
                    borderWidth={'0px 1px'}
                    borderStyle={'solid'}
                    borderColor={'cyan.web'}
                    borderRadius={'8px'}
                  ></Box>
                  <Text
                    as='h2'
                    textStyle={{ base: 'mobile.body.medium.xs', md: 'desktop.body.medium.xs' }}
                    px={{ base: '0' }}
                    color={headlineColor ? headlineColor.headline : 'mountain'}
                  >
                    {hyphenHeadline && hyphenHeadline.toLocaleUpperCase()}
                  </Text>
                </Box>
              )}
            </Flex>
            {!!model.elements.subheadline.value.length && (
              <Flex order={2}>
                <Text
                  as='h3'
                  textStyle={{ base: 'mobile.body.s', md: 'desktop.body.l' }}
                  px={{ base: 'xs', lg: 0 }}
                  pt={{ base: 'xs', lg: 'xs' }}
                >
                  {subHeadline}
                </Text>
              </Flex>
            )}

            {/* --------- HEADLINE AND VIEW MORE END ---------  */}

            {/* --------- ITEMS ---------  */}
            {items.length < 3 || shouldShowOffices || !isSmallScreen ? (
              <Grid
                as='ul'
                listStyleType='none'
                templateColumns={{
                  base: 'repeat(1, 1fr)',
                  lg: shouldShowPeople ? `repeat(3, 1fr)` : `repeat(${items.length}, 1fr)`,
                }}
                gap={{ base: shouldShowOffices ? 'mobile.m' : 'mobile.l', lg: 'desktop.xl' }}
                mt={
                  items.length === 1
                    ? {}
                    : model.elements.subheadline.value.length
                    ? { base: 'xs', lg: 'desktop.xs' }
                    : { base: 's', lg: 'desktop.m' }
                }
                order={3}
                flexBasis={'100%'}
              >
                {getOverviewItems()}
              </Grid>
            ) : (
              <Flex
                mt={{ base: 's', lg: 'l' }}
                w='100%'
                as='ul'
                listStyleType='none'
                flexDir={'row'}
                pb='s'
                overflow='auto'
                sx={{
                  '::-webkit-scrollbar': {
                    bg: 'transparent',
                    height: '3px',
                  },
                  '::-webkit-scrollbar-thumb': {
                    bg: 'var(--chakra-colors-mountain-20)',
                  },
                }}
                order={2}
                flexBasis={'100%'}
              >
                {getOverviewItems(true)}
              </Flex>
            )}

            <Box
              order={1}
              display={'flex'}
              marginLeft={'auto'}
              zIndex={1}
              alignItems={'center'}
              justifyContent='flex-end'
            >
              <Flex justifyContent='flex-end' alignItems={'center'}>
                {linkToMore !== '' && !!model.elements.cta.value.length && (
                  <Link
                    ariaLabel={ariaLinkText}
                    href={linkToMore}
                    weight='680'
                    type={LinkType.SecondaryButton}
                    iconName='chevronRightForLink'
                    locale={router.locale}
                    isPrDisabled={true}
                  >
                    {model.elements.cta.value}
                  </Link>
                )}
              </Flex>
            </Box>

            {/* --------- ITEMS END ---------  */}
          </Container>
        }
      </Box>
    </>
  );
};
